import { z } from 'zod';
import { Money } from './money';
import { Features } from './account';

export const CatalogCategory = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  sort: z.number().int(),
  /** цех бегунка: kitchen | bar; null — берётся у родителя */
  station: z.string().nullable().optional(),
  /** родительская группа: «Бар → Напитки». null — сама группа */
  parentId: z.string().nullable(),
  /** наценка группы, %; null — брать у родителя */
  markupPct: z.number().int().nullable().optional(),
  /** запрет скидок: касса не даст скидку на товары этой группы */
  allowDiscount: z.boolean().optional(),
  writeOffOnSale: z.boolean().optional(),
});

export const CatalogModifier = z.object({
  id: z.string(),
  name: z.string(),
  priceDelta: Money,
  /** себестоимость добавки — касса кладёт её в снапшот продажи */
  cost: Money.optional(),
  /** «без лука»: этот ингредиент в этом количестве не расходуется */
  removes: z.object({ productId: z.string(), qty: z.number() }).optional(),
});

export const CatalogModifierGroup = z.object({
  id: z.string(),
  name: z.string(),
  minSelect: z.number().int(),
  maxSelect: z.number().int(),
  options: z.array(CatalogModifier),
});

export const CatalogProduct = z.object({
  id: z.string(),
  categoryId: z.string(),
  name: z.string(),
  price: Money,
  unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']),
  modifierGroupIds: z.array(z.string()),
});

export const PromoRule = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('HAPPY_HOURS'),
    percent: z.number().int().min(1).max(100),
    days: z.array(z.number().int().min(0).max(6)).min(1),
    fromMin: z.number().int().min(0).max(1439),
    toMin: z.number().int().min(1).max(1440),
  }),
  z.object({
    type: z.literal('N_PLUS_ONE'),
    productId: z.string(),
    n: z.number().int().min(1),
  }),
]);
export type PromoRule = z.infer<typeof PromoRule>;

export const CatalogPromo = z.object({
  id: z.string(),
  name: z.string(),
  rule: PromoRule,
});
export type CatalogPromo = z.infer<typeof CatalogPromo>;

export const CatalogResponse = z.object({
  categories: z.array(CatalogCategory),
  products: z.array(CatalogProduct),
  modifierGroups: z.array(CatalogModifierGroup),
  promos: z.array(CatalogPromo),
  /** возможности бизнеса — касса подстраивает режимы и стол */
  features: Features,
  /** productId → остаток на стопе; null = полностью в стопе. */
  stopList: z.record(z.string(), z.number().nullable()),
  /** когда поставлен стоп: id → ISO — касса видит давние позиции */
  stopSince: z.record(z.string(), z.string()).optional(),
  /**
   * Замок подписки: заведение не оплатило, продажи закрыты.
   *
   * Поле необязательное — старые кассы его не увидят и продолжат
   * работать, это осознанно: обновление кассы не должно быть
   * условием оплаты.
   */
  lock: z.object({
    /** WARN — предупреждаем заранее, BLOCK — продажи закрыты */
    kind: z.enum(['WARN', 'BLOCK']),
    blocked: z.boolean(),
    /** что показать человеку — его словами, без «подписка неактивна» */
    message: z.string(),
    /** сколько дней осталось (WARN) или сколько просрочено (BLOCK) */
    days: z.number().int(),
    /** дата, до которой оплачено — чтобы сказать точно */
    paidUntil: z.string().nullable(),
  }).optional(),
  /** Сбор за обслуживание — правило приходит с каталогом, а не из копии */
  service: z.object({
    percent: z.number().int().min(0).max(30),
    dineInOnly: z.boolean(),
    fromGuests: z.number().int().min(0),
  }).optional(),
});
export type CatalogResponse = z.infer<typeof CatalogResponse>;
